export interface AgeConcern {
  question: string;
  answer: string;
  links: readonly { href: string; label: string }[];
}

const SEVEN_YEARS_ARTICLE_LINK = {
  href: "/articles/cat-age-7-years/",
  label: "猫の7歳は人間でいうと何歳？中高年期（7〜10歳）に変わること",
};

const SENIOR_ARTICLE_LINK = {
  href: "/articles/cat-lifespan-and-senior-age/",
  label: "猫の平均寿命は？15歳・20歳は人間でいうと何歳か",
};

const CALORIE_LINK = {
  href: "/calorie/",
  label: "体重からごはんの量を計算する",
};

const FOOD_TRANSITION_LINK = {
  href: "/calorie/transition/",
  label: "フードの切り替えプランを作る",
};

const BODY_CONDITION_LINK = {
  href: "/body-condition/",
  label: "猫の体型をチェックする",
};

const KITTEN_FOOD_SWITCH: AgeConcern = {
  question: "子猫用のフードはいつまで？",
  answer:
    "子猫期は1歳までで、1歳前後が成猫用のフードに切り替える時期です。急に変えると吐いたり下痢をしたりすることがあるため、環境省のガイドラインでは1〜2週間かけて新しいフードの割合を少しずつ増やす方法を紹介しています。",
  links: [
    FOOD_TRANSITION_LINK,
    {
      href: "/articles/kitten-age-by-month/",
      label: "子猫の月齢は人間でいうと何歳？生後1〜12か月の成長と節目",
    },
  ],
};

const WEIGHT_GAIN_AFTER_NEUTERING: AgeConcern = {
  question: "避妊・去勢後に太ってきた？",
  answer:
    "避妊・去勢済みの成猫が1日に必要なエネルギー量は、安静時の量（RER）の1.2〜1.4倍が目安です。体重だけでなく体型（BCS）も見て、ごはんの量を調整します。",
  links: [BODY_CONDITION_LINK, CALORIE_LINK],
};

const TREAT_AMOUNT: AgeConcern = {
  question: "おやつはどれくらいまで？",
  answer:
    "環境省のガイドラインでは、おやつは1日に必要なエネルギー量の20%以内とし、与えた分だけ主食を減らすとしています。WSAVAのガイドラインでは、おやつが総カロリーの10%をこえることが、獣医師が栄養状態を詳しく確認する目安の1つです。体重4kgで避妊・去勢済みの猫なら、10%で約24kcal、20%で約48kcalです。",
  links: [
    {
      href: "/articles/cat-treats-and-complementary-food/",
      label: "猫のおやつはどれくらいまで？一般食・おやつの量の目安",
    },
    CALORIE_LINK,
  ],
};

const WEIGHT_LOSS_DIET: AgeConcern = {
  question: "ダイエットさせたほうがいい？",
  answer:
    "AAHAの栄養・体重管理ガイドラインでは、太っている猫のごはんの量は、今の体重ではなく理想体重から計算することをすすめています。減量は健康状態によってリスクを伴うこともあるため、まず体型を確かめ、進め方は獣医師と相談しましょう。",
  links: [BODY_CONDITION_LINK, CALORIE_LINK],
};

const BASELINE_CHECKUP: AgeConcern = {
  question: "健康なうちに検査は必要？",
  answer:
    "AAHA・AAFPのガイドラインは、年齢にかかわらず最低でも年1回の診察をすすめています。成猫期のうちに血液や尿の検査値を1回とっておくと、年をとってからの変化を比べる基準になります。",
  links: [SEVEN_YEARS_ARTICLE_LINK],
};

const BEFORE_MATURE_ADULT: AgeConcern = {
  question: "7歳から何が変わる？",
  answer:
    "7〜10歳は中高年期にあたります。1日に必要なエネルギー量が安静時の量（RER）と同じくらいまで下がる猫が多く、血液・尿・血圧・甲状腺の検査も1〜2年ごとが目安になります。",
  links: [SEVEN_YEARS_ARTICLE_LINK],
};

const SENIOR_FOOD_TIMING: AgeConcern = {
  question: "シニアフードに変える時期？",
  answer:
    "環境省のペットフード・ガイドラインでは8〜10歳くらいからを中高齢期とし、低エネルギー・低脂肪の高齢期用フードを紹介しています。年齢だけで決めず体重と体型を見て判断し、切り替えるときは1〜2週間かけて少しずつ混ぜます。",
  links: [FOOD_TRANSITION_LINK, SEVEN_YEARS_ARTICLE_LINK],
};

const WEIGHT_GAIN_ON_SAME_FOOD: AgeConcern = {
  question: "同じ量なのに太ってきた？",
  answer:
    "AAHA・AAFPのガイドラインでは、7〜10歳の猫が1日に必要なエネルギー量は安静時の量（RER）と同じくらいになる場合があるとしています。成猫期と同じ量を食べ続けると、必要な量をこえやすくなります。",
  links: [{ href: "/calorie/", label: "中高年期の係数でごはんの量を計算する" }],
};

const MATURE_ADULT_CHECKUP: AgeConcern = {
  question: "健康診断の検査は増やすべき？",
  answer:
    "AAHA・AAFPのガイドラインでは、7〜10歳の猫は血液・尿・血圧・甲状腺などの検査を1〜2年ごとに受けるのが目安です。1回ごとの値だけでなく、年ごとの変化を見ることが重視されています。",
  links: [SEVEN_YEARS_ARTICLE_LINK],
};

const DENTAL_CHANGES: AgeConcern = {
  question: "口臭や食べ方が気になる？",
  answer:
    "AAHA・AAFPのガイドラインは、中高年期以降に注意する病気として歯周病と歯の吸収（歯が溶ける病気）を挙げています。口臭や食べ方の変化に気づいたら、診察で獣医師に伝えましょう。",
  links: [SEVEN_YEARS_ARTICLE_LINK],
};

const BEFORE_SENIOR: AgeConcern = {
  question: "シニア期で何が変わる？",
  answer:
    "AAHA・AAFPのガイドラインでは、10歳をこえると診察は少なくとも半年に1回が目安になります。1日に必要なエネルギー量は安静時の量（RER）の1.1〜1.2倍と、中高年期よりむしろ増えます。",
  links: [SENIOR_ARTICLE_LINK],
};

const WEIGHT_LOSS: AgeConcern = {
  question: "痩せてきたのは年のせい？",
  answer:
    "AAHA・AAFPのガイドラインでは、シニア期の猫は消化の力が落ちてやせてくることが多く、やせすぎがよくある問題とされています。必要なエネルギー量は安静時の量（RER）の1.1〜1.2倍（場合によっては1.25倍）が目安で、体重と体型の記録が変化に気づく手がかりになります。",
  links: [BODY_CONDITION_LINK, CALORIE_LINK],
};

const SENIOR_CHECKUP: AgeConcern = {
  question: "健康診断は半年に1回？",
  answer:
    "AAHA・AAFPのガイドラインでは、シニア期の猫は少なくとも半年に1回の診察がすすめられています。血液・尿・甲状腺・腎臓の指標・血圧の検査も、少なくとも年1回、できれば半年ごとが目安です。",
  links: [SENIOR_ARTICLE_LINK],
};

const STOPPED_JUMPING: AgeConcern = {
  question: "高いところに上らなくなった？",
  answer:
    "AAHA・AAFPのガイドラインでは、台やお気に入りの窓辺に上らなくなることを、関節の病気による痛みのサインの可能性として挙げています。関節の病気は年をとった猫ほど多く、重くなるとされています。",
  links: [SENIOR_ARTICLE_LINK],
};

const LOSS_OF_APPETITE: AgeConcern = {
  question: "食欲が落ちてきた？",
  answer:
    "年をとるとにおいや味を感じる力が落ち、食欲が下がることが多くなります。環境省のガイドラインでは、フードを人肌に温める、お湯でふやかすといった工夫を紹介しています。",
  links: [SENIOR_ARTICLE_LINK],
};

const TOILET_CHANGES: AgeConcern = {
  question: "トイレの使い方が変わった？",
  answer:
    "AAHA・AAFPのガイドラインでは、原因として尿路の病気、便秘、糖尿病、筋力の低下、バランスの悪さ、痛みを挙げています。受診とあわせて、縁の低いトイレや階段を使わずに行ける場所に置くことも負担を減らします。",
  links: [SENIOR_ARTICLE_LINK],
};

const NIGHT_VOCALIZATION: AgeConcern = {
  question: "夜中に鳴くようになった？",
  answer:
    "AAHA・AAFPのガイドラインでは、夜中に起きて鳴く原因として、聴覚や視覚の衰え、認知機能不全症候群、痛み、甲状腺機能亢進症、高血圧を挙げています。様子だけでは原因を特定できないため、気づいた変化を獣医師に伝えましょう。",
  links: [SENIOR_ARTICLE_LINK],
};

const AVERAGE_LIFESPAN: AgeConcern = {
  question: "猫の平均寿命は何歳？",
  answer:
    "ペットフード協会の調査では、2025年の猫の平均寿命は16.00歳です。16歳以上の猫は、飼われている猫の6.8%にあたります。",
  links: [SENIOR_ARTICLE_LINK],
};

/**
 * 年齢ごとに、飼い主が気にかけやすい変化を並べる。
 * 先頭の項目はページのタイトルに、先頭2つはdescriptionに使う。
 */
const AGE_CONCERNS: ReadonlyMap<number, readonly AgeConcern[]> = new Map([
  [1, [KITTEN_FOOD_SWITCH, WEIGHT_GAIN_AFTER_NEUTERING, BASELINE_CHECKUP]],
  [2, [WEIGHT_GAIN_AFTER_NEUTERING, TREAT_AMOUNT, BASELINE_CHECKUP]],
  [3, [TREAT_AMOUNT, WEIGHT_GAIN_AFTER_NEUTERING, WEIGHT_LOSS_DIET]],
  [4, [WEIGHT_LOSS_DIET, TREAT_AMOUNT, BASELINE_CHECKUP]],
  [5, [BASELINE_CHECKUP, WEIGHT_LOSS_DIET, WEIGHT_GAIN_AFTER_NEUTERING]],
  [6, [BEFORE_MATURE_ADULT, WEIGHT_GAIN_AFTER_NEUTERING, BASELINE_CHECKUP]],
  [7, [SENIOR_FOOD_TIMING, MATURE_ADULT_CHECKUP, WEIGHT_GAIN_ON_SAME_FOOD]],
  [8, [WEIGHT_GAIN_ON_SAME_FOOD, SENIOR_FOOD_TIMING, DENTAL_CHANGES]],
  [9, [DENTAL_CHANGES, WEIGHT_GAIN_ON_SAME_FOOD, MATURE_ADULT_CHECKUP]],
  [10, [BEFORE_SENIOR, DENTAL_CHANGES, WEIGHT_GAIN_ON_SAME_FOOD]],
  [11, [WEIGHT_LOSS, SENIOR_CHECKUP, STOPPED_JUMPING]],
  [12, [STOPPED_JUMPING, WEIGHT_LOSS, SENIOR_CHECKUP]],
  [13, [LOSS_OF_APPETITE, WEIGHT_LOSS, STOPPED_JUMPING]],
  [14, [TOILET_CHANGES, LOSS_OF_APPETITE, SENIOR_CHECKUP]],
  [15, [NIGHT_VOCALIZATION, WEIGHT_LOSS, TOILET_CHANGES]],
  [16, [AVERAGE_LIFESPAN, NIGHT_VOCALIZATION, LOSS_OF_APPETITE]],
  [17, [TOILET_CHANGES, AVERAGE_LIFESPAN, NIGHT_VOCALIZATION]],
  [18, [NIGHT_VOCALIZATION, LOSS_OF_APPETITE, AVERAGE_LIFESPAN]],
  [19, [LOSS_OF_APPETITE, TOILET_CHANGES, AVERAGE_LIFESPAN]],
  [20, [AVERAGE_LIFESPAN, NIGHT_VOCALIZATION, LOSS_OF_APPETITE]],
]);

/**
 * 同じページに同じリンクが並ばないよう、先に出た問いで張ったリンクは後の問いから外す。
 */
export function ageConcernsOf(years: number): readonly AgeConcern[] {
  const concerns = AGE_CONCERNS.get(years);
  if (!concerns) {
    throw new Error(`age concerns are not defined: ${years}`);
  }
  const shownHrefs = new Set<string>();
  return concerns.map((concern) => {
    const links = concern.links.filter((link) => !shownHrefs.has(link.href));
    links.forEach((link) => shownHrefs.add(link.href));
    return { ...concern, links };
  });
}

export const AGE_CONCERN_SOURCES: readonly { title: string; url: string }[] = [
  {
    title: "AAHA/AAFP「2021 AAHA/AAFP Feline Life Stage Guidelines」",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10812130/",
  },
  {
    title: "AAHA「2021 AAHA Nutrition and Weight Management Guidelines」",
    url: "https://www.aaha.org/resources/2021-aaha-nutrition-and-weight-management-guidelines/weight-reduction-in-the-obese-pet/",
  },
  {
    title: "WSAVA「Nutritional Assessment Guidelines」（2011年、PDF）",
    url: "https://wsava.org/wp-content/uploads/2020/01/WSAVA-Nutrition-Assessment-Guidelines-2011-JSAP.pdf",
  },
  {
    title:
      "環境省「飼い主のためのペットフード・ガイドライン ～犬・猫の健康を守るために～」",
    url: "https://www.env.go.jp/nature/dobutsu/aigo/2_data/pamph/petfood_guide_1808.html",
  },
  {
    title:
      "一般社団法人ペットフード協会「令和7年 全国犬猫飼育実態調査」主要指標サマリー（PDF）",
    url: "https://petfood.or.jp/pdf/data/2025/3.pdf",
  },
];
